import { useState } from 'react';
import { App, Button, Flex, Input, Space, Typography } from 'antd';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { LinesGrid } from '@/shared/ui/LinesGrid';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { useCreateCounterpartyMutation } from '@/entities/counterparty';
import { translateApiError } from '@/shared/api/errorToMessage';

export interface CounterpartyCreateWindowProps {
  onClose: () => void;
}

interface Row {
  key: string;
  name: string;
  phone: string;
}

let keySeq = 0;
function emptyRow(): Row {
  keySeq += 1;
  return { key: `cp${keySeq}-${Date.now()}`, name: '', phone: '' };
}

/** §2.2: добавление контрагентов — плавающее окно (двигается/растягивается, как окна документов)
 *  с табличной формой вместо одиночной antd-модалки, позволяет ввести сразу несколько строк и
 *  сохранить всех разом; строка снизу добавляется автоматически по заполнению «Название»
 *  (тот же приём, что и автодобавление строки товара в документе). */
export function CounterpartyCreateWindow({ onClose }: CounterpartyCreateWindowProps) {
  const { message } = App.useApp();
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [createCounterparty, { isLoading }] = useCreateCounterpartyMutation();

  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }

  function removeRow(key: string) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }

  function updateName(key: string, rowIndex: number, value: string) {
    setRows((prev) => {
      const wasEmpty = !prev[rowIndex]?.name;
      const next = prev.map((r) => (r.key === key ? { ...r, name: value } : r));
      if (rowIndex === prev.length - 1 && wasEmpty && value.trim()) {
        return [...next, emptyRow()];
      }
      return next;
    });
  }

  function updatePhone(key: string, value: string) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, phone: value } : r)));
  }

  const filled = rows.filter((r) => r.name.trim());

  async function handleSave() {
    if (filled.length === 0) return;
    try {
      await Promise.all(
        filled.map((r) =>
          createCounterparty({ name: r.name.trim(), phone: r.phone.trim() || undefined, isSupplier: true, isCustomer: true }).unwrap(),
        ),
      );
      message.success(filled.length > 1 ? `Добавлено контрагентов: ${filled.length}` : 'Контрагент добавлен');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  const columns: LinesGridColumn<Row>[] = [
    {
      key: 'name',
      title: 'Название',
      width: 260,
      render: (row, rowIndex) => (
        <Input
          className="nav-cell"
          data-nav-row={rowIndex}
          data-nav-col={0}
          value={row.name}
          autoFocus={rowIndex === 0}
          onChange={(e) => updateName(row.key, rowIndex, e.target.value)}
        />
      ),
    },
    {
      key: 'phone',
      title: 'Телефон',
      width: 180,
      render: (row, rowIndex) => (
        <Input
          className="nav-cell"
          data-nav-row={rowIndex}
          data-nav-col={1}
          value={row.phone}
          onChange={(e) => updatePhone(row.key, e.target.value)}
        />
      ),
    },
  ];

  return (
    <FloatingWindow
      kind="counterpartyCreate"
      title="Новые контрагенты"
      onClose={onClose}
      footer={
        <Space>
          <Button type="primary" loading={isLoading} disabled={filled.length === 0} onClick={() => void handleSave()}>
            Сохранить{filled.length > 1 ? ` (${filled.length})` : ''}
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      }
    >
      <Flex vertical gap={8}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Заполните название — строка снизу добавится автоматически. Пустые строки не сохраняются.
        </Typography.Text>
        <LinesGrid lines={rows} columns={columns} getRowKey={(r) => r.key} onAddRow={addRow} onRemoveRow={removeRow} />
      </Flex>
    </FloatingWindow>
  );
}
